// Convert-Operationen zwischen konvertierbaren Canvas-Objekt-Typen.
// "Konvertierbar" wird in OBJECT_META.isConvertible gepflegt — wird hier nur ausgelesen.

import {
  type CanvasObject,
  OBJECT_META,
  type ObjectType,
  parseCanvasObject,
} from "@ms-whiteboard/shared";

export type ConvertibleType =
  | "sticky"
  | "text"
  | "shape-rect"
  | "requirement"
  | "pain"
  | "dependency";

export const CONVERTIBLE_TYPES: readonly ConvertibleType[] = (
  Object.keys(OBJECT_META) as ObjectType[]
).filter((t): t is ConvertibleType => OBJECT_META[t].isConvertible);

export function isConvertibleType(t: string): t is ConvertibleType {
  return t in OBJECT_META && OBJECT_META[t as ObjectType].isConvertible;
}

function extractText(obj: CanvasObject): string {
  const field = OBJECT_META[obj.type].textField;
  if (!field) return "";
  const raw = (obj as Record<string, unknown>)[field];
  return typeof raw === "string" ? raw : "";
}

function extractSize(obj: CanvasObject): { width?: number; height?: number } {
  const o = obj as { width?: unknown; height?: unknown };
  const out: { width?: number; height?: number } = {};
  if (typeof o.width === "number") out.width = o.width;
  if (typeof o.height === "number") out.height = o.height;
  return out;
}

export function convertObject(
  obj: CanvasObject,
  target: ConvertibleType,
  textFallback = "",
): CanvasObject | null {
  if (!isConvertibleType(obj.type)) return null;
  if (obj.type === target) return null;

  const text = extractText(obj) || textFallback;
  const size = extractSize(obj);
  const base = {
    id: obj.id,
    x: obj.x,
    y: obj.y,
    parentRegionId: obj.parentRegionId,
    createdBy: obj.createdBy,
    createdAt: obj.createdAt,
  };

  const targetMeta = OBJECT_META[target];
  // Falls Quelle keine Groesse hatte und Ziel explicit-size verlangt, fallen wir auf
  // OBJECT_META.defaultSize des Ziels zurueck (z.B. shape-rect 160x80).
  const width =
    size.width ?? (targetMeta.hasExplicitSize ? targetMeta.defaultSize.width : undefined);
  const height =
    size.height ?? (targetMeta.hasExplicitSize ? targetMeta.defaultSize.height : undefined);

  let next: unknown;
  switch (target) {
    case "sticky":
      next = {
        ...base,
        type: "sticky",
        text,
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
      };
      break;
    case "text":
      next = {
        ...base,
        type: "text",
        text,
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
      };
      break;
    case "shape-rect":
      next = {
        ...base,
        type: "shape-rect",
        label: text,
        width,
        height,
      };
      break;
    case "requirement":
      next = {
        ...base,
        type: "requirement",
        text,
        priority: "should",
        category: "functional",
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
      };
      break;
    case "pain":
      next = {
        ...base,
        type: "pain",
        text,
        severity: "med",
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
      };
      break;
    case "dependency":
      next = {
        ...base,
        type: "dependency",
        text,
        kind: "internal",
        status: "open",
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
      };
      break;
  }
  return parseCanvasObject(next);
}
